/**
 * Marketing-consent record appended to every Web3Forms submission.
 *
 * Phase 1 of consent capture: the checkbox state travels with the enquiry email
 * so there is a written record of who opted in, to which wording, when, where
 * and in which language. A CRM sync comes later and should read these same
 * field names.
 *
 * Bump CONSENT_VERSION whenever MARKETING_CONSENT_TEXT or the privacy notice's
 * marketing section changes, so each stored consent can be matched to the exact
 * wording the person agreed to.
 */

export const CONSENT_VERSION = "2026-09-24";

/** The exact English wording shown next to the checkbox (and the i18n key). */
export const MARKETING_CONSENT_TEXT =
  "Yes, Anchor Sprint may email me about events, promotions and new products. I can unsubscribe at any time.";

export const PRIVACY_NOTICE_PATH = "/privacy-policy/";

export interface MarketingConsentFields {
  marketing_consent: "Yes" | "No";
  marketing_consent_text: string;
  marketing_consent_version: string;
  marketing_consent_at: string;
  marketing_consent_page: string;
  marketing_consent_locale: string;
}

/** Fields to add to a submission. Call at submit time so the timestamp is accurate. */
export function getMarketingConsentFields(
  consented: boolean,
  locale: string = "en",
): MarketingConsentFields {
  return {
    marketing_consent: consented ? "Yes" : "No",
    marketing_consent_text: MARKETING_CONSENT_TEXT,
    marketing_consent_version: CONSENT_VERSION,
    marketing_consent_at: new Date().toISOString(),
    marketing_consent_page:
      typeof window !== "undefined" ? window.location.href : "",
    marketing_consent_locale: locale,
  };
}

/** Appends the consent fields to a Web3Forms FormData payload. */
export function appendMarketingConsent(
  body: FormData,
  consented: boolean,
  locale: string = "en",
): void {
  Object.entries(getMarketingConsentFields(consented, locale)).forEach(
    ([name, value]) => body.append(name, value),
  );
}

/** Human-readable line for the email body, e.g. "Marketing consent: No". */
export function getMarketingConsentLine(consented: boolean): string {
  return `Marketing consent: ${consented ? "Yes" : "No"}`;
}

/** Adds the consent line to the end of a message body. */
export function withMarketingConsentLine(
  message: string,
  consented: boolean,
): string {
  const line = getMarketingConsentLine(consented);
  return message.trim() ? `${message}\n\n${line}` : line;
}
